import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useDirectory, useUpdateMemberRole, useInviteMember, useRemoveMember, useNodePendingInvitations, useRevokeInvitation } from '../hooks/useDirectory';
import { useCreateDirectChannel } from '../hooks/useChat';
import { DirectoryMember, InvitationResponse } from '../types';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Avatar } from '../components/ui/Avatar';
import { Spinner } from '../components/ui/Spinner';
import { Input } from '../components/ui/Input';
import { 
  Search, 
  UserPlus, 
  Mail, 
  Clock, 
  X,
  Filter,
  Phone,
  Lock,
  HelpCircle,
  Check,
  Copy,
  Upload
} from 'lucide-react';
import toast from 'react-hot-toast';
import { safeFormat } from '../lib/utils';
import { Modal } from '../components/ui/Modal';
import { usePermissions } from '../hooks/usePermissions';
import { useCurrentUser } from '../contexts/AuthContext';
import { MemberListItem } from '../components/members/MemberListItem';
import { RosterCsvImportModal } from '../components/members/RosterCsvImportModal';

export const MembersPage = () => {
  const { node, getTerm } = useTenant();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can, isAdmin, isLocalAdmin, isAtLeastModerator } = usePermissions();
  // Terminology is per-organization (e.g. "Soldiers", "Employees"), so every
  // member-facing string routes through it instead of hardcoding "Member".
  const memberLabel = getTerm('member');
  const membersLabel = `${memberLabel}s`;
  const canManageRoles = isAdmin || can('ROLE_MANAGE');
  const canManageMembers = isAdmin || can('MEMBER_MANAGE');
  const canInviteMembers = isAdmin || can('MEMBER_INVITE');

  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedRole, setSelectedRole] = React.useState<string>('ALL');
  const [selectedStatus, setSelectedStatus] = React.useState<string>('ALL');
  const [isInviteModalOpenManual, setIsInviteModalOpenManual] = React.useState(false);
  const [isCsvModalOpen, setIsCsvModalOpen] = React.useState(false);
  const isInviteModalOpen = isInviteModalOpenManual || searchParams.get('action') === 'invite';
  const setIsInviteModalOpen = React.useCallback((open: boolean) => {
    setIsInviteModalOpenManual(open);
    if (!open && searchParams.get('action') === 'invite') {
      setSearchParams(params => {
        const next = new URLSearchParams(params);
        next.delete('action');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [createdInvite, setCreatedInvite] = React.useState<InvitationResponse | null>(null);
  const [selectedMember, setSelectedMember] = React.useState<DirectoryMember | null>(null);
  const [activeActionMenu, setActiveActionMenu] = React.useState<string | null>(null);
  const [roleChangeMember, setRoleChangeMember] = React.useState<DirectoryMember | null>(null);
  const [newTargetRole, setNewTargetRole] = React.useState<string>('MEMBER');
  
  const { data, isLoading } = useDirectory(node?.id || '', {
    name: searchQuery,
    includeDescendants: false
  });

  const { data: pendingData, isLoading: isPendingLoading } = useNodePendingInvitations(node?.id || '');
  const isTableLoading = isLoading || (isPendingLoading && !data?.members);
  const inviteMutation = useInviteMember(node?.id || '');
  const revokeInvitation = useRevokeInvitation(node?.id || '');
  const updateMemberRole = useUpdateMemberRole(node?.id || '');
  const removeMember = useRemoveMember(node?.id || '');
  const createDirectChannel = useCreateDirectChannel(node?.id || '');
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState('MEMBER');

  const handleSendMessage = (member: DirectoryMember) => {
    if (!member.userId) {
      toast.error('User does not have an active chat profile');
      return;
    }
    const currentUserId = user?.id || user?.userId;
    if (member.userId === currentUserId) {
      toast.error('Cannot send a direct message to yourself');
      return;
    }
    createDirectChannel.mutate(member.userId, {
      onSuccess: (channel) => {
        setSelectedMember(null);
        navigate(`/chat?channel=${channel.id}`);
      },
      onError: () => {
        toast.error('Failed to initiate direct message');
      }
    });
  };

  const handleExecuteRoleChange = () => {
    if (!roleChangeMember || !roleChangeMember.userId) return;
    updateMemberRole.mutate({
      userId: roleChangeMember.userId,
      role: newTargetRole
    }, {
      onSuccess: () => {
        toast.success(`Role updated to ${newTargetRole}`);
        setRoleChangeMember(null);
      },
      onError: () => {
        toast.error('Failed to update role');
      }
    });
  };

  const handleSendInvite = () => {
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }
    inviteMutation.mutate({ email: inviteEmail, role: inviteRole }, {
      onSuccess: (response: InvitationResponse) => {
        const enrichedInvite = {
          ...response,
          inviteLink: response?.inviteLink || (response?.token ? `${window.location.origin}/join?token=${response.token}` : `${window.location.origin}/join`)
        };
        setCreatedInvite(enrichedInvite);
        setInviteEmail('');
      }
    });
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
    setCreatedInvite(null);
    setInviteEmail('');
  };

  const rawMembers = data?.members;
  const members = React.useMemo(() => {
    if (Array.isArray(rawMembers)) return rawMembers;
    return (rawMembers as any)?.content || [];
  }, [rawMembers]);

  const existingEmails = React.useMemo(() => {
    return new Set<string>(
      members
        .map((m: any) => (m.email ? String(m.email).toLowerCase() : ''))
        .filter((email: string): boolean => email.length > 0)
    );
  }, [members]);

  const pendingAsMembers: DirectoryMember[] = React.useMemo(() => {
    return (pendingData?.invitations || [])
      .filter((inv) => inv.email && !existingEmails.has(inv.email.toLowerCase()))
      .map((inv) => {
        const inviteUrl = inv.inviteLink || (inv.token ? `${window.location.origin}/join?token=${inv.token}` : undefined);
        return {
          id: inv.id,
          userId: `invite-${inv.id}`,
          name: inv.email.split('@')[0],
          email: inv.email,
          role: inv.role,
          nodeId: inv.nodeId || node?.id || '',
          nodeName: inv.nodeName || node?.name || '',
          joinedAt: inv.createdAt || new Date().toISOString(),
          skills: [],
          inviteLink: inviteUrl,
          metadata: {
            status: 'PENDING',
            invitedBy: inv.inviterId,
            token: inv.token,
            inviteLink: inviteUrl,
            invitationId: inv.id
          }
        };
      });
  }, [pendingData?.invitations, existingEmails, node?.id, node?.name]);

  // Filtered members by role and status, including pending invitations
  const filteredMembers = React.useMemo(() => {
    let combined = [...members];
    if (selectedStatus === 'ALL' || selectedStatus === 'PENDING') {
      combined = [...combined, ...pendingAsMembers];
    }
    return combined.filter((m: any) => {
      const isPending = m.metadata?.status === 'PENDING' || m.status === 'PENDING';
      const matchesRole = selectedRole === 'ALL' || m.role === selectedRole;
      const matchesStatus = selectedStatus === 'ALL' || (selectedStatus === 'PENDING' ? isPending : !isPending);
      const matchesSearch = !searchQuery || 
        (m.name && m.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (m.email && m.email.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesRole && matchesStatus && matchesSearch;
    });
  }, [members, pendingAsMembers, selectedRole, selectedStatus, searchQuery]);

  const roles = node?.config?.roleDefinitions?.map(r => r.name) || ['ADMIN', 'MODERATOR', 'MEMBER'];

  const isLeadership = (roleName: string) => {
    return ['ADMIN', 'OWNER', 'PRESIDENT', 'COUNCIL'].includes(roleName.toUpperCase());
  };

  const getVisibilityStatus = (member: DirectoryMember) => {
    // If current user is leadership, they see everything
    if (isAtLeastModerator) return 'FULL';
    
    // Leadership roles are protected by default from normal members
    if (isLeadership(member.role)) return 'RESTRICTED';

    // Check individual privacy setting (from metadata)
    if (member.metadata?.privacy === 'PRIVATE' || member.metadata?.isPrivate === true) {
      return 'RESTRICTED';
    }

    // Default for members-to-members: restricted by user request ("privileged information")
    return 'RESTRICTED';
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{membersLabel}</h1>
          <p className="text-sm text-gray-500">Manage your organization's {membersLabel.toLowerCase()} and their permissions.</p>
        </div>
        {canInviteMembers && (
          <div className="flex flex-wrap items-center gap-3">
            <Button 
              variant="secondary" 
              onClick={() => setIsCsvModalOpen(true)}
              className="border-2 border-black dark:border-white shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#fff]"
            >
              <Upload size={18} className="mr-2" />
              Import CSV
            </Button>
            <Button onClick={() => setIsInviteModalOpen(true)}>
              <UserPlus size={18} className="mr-2" />
              Invite {memberLabel}
            </Button>
          </div>
        )}
      </div>

      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input 
              placeholder={`Search ${membersLabel.toLowerCase()}...`} 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter size={18} className="text-gray-400" />
              <select 
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="bg-white border-2 border-black dark:border-white rounded-none px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-primary/20 dark:bg-slate-900"
              >
                <option value="ALL">All Roles</option>
                {roles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
            
            <select 
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="bg-white border-2 border-black dark:border-white rounded-none px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-primary/20 dark:bg-slate-900"
            >
              <option value="ALL">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>
        </div>

        {/* Results */}
        {isTableLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner size="lg" />
            <p className="mt-4 text-sm text-gray-500">Loading {membersLabel.toLowerCase()}...</p>
          </div>
        ) : filteredMembers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-black dark:border-white text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  <th className="px-4 py-3">{memberLabel}</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredMembers.map((member: any) => {
                  const isPending = member.metadata?.status === 'PENDING';
                  const currentUserId = user?.id || user?.userId || user?.clerkId;
                  const isCurrentUser = Boolean(currentUserId && member.userId === currentUserId);
                  const effectiveMember = isCurrentUser ? {
                    ...member,
                    name: (member.name && member.name !== member.userId) ? member.name : (user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || member.name),
                    email: member.email || user?.email || user?.primaryEmailAddress?.emailAddress || member.email
                  } : member;
                  const isInviter = (member.invitedBy && member.invitedBy === currentUserId) || (member.metadata?.invitedBy && member.metadata?.invitedBy === currentUserId);
                  const canRevoke = isPending && (isInviter || isAdmin || isLocalAdmin || canManageMembers);

                  return (
                    <MemberListItem
                      key={member.id}
                      member={effectiveMember}
                      isAtLeastModerator={isAtLeastModerator}
                      canManageRoles={canManageRoles}
                      canManageMembers={canManageMembers}
                      canRevoke={canRevoke}
                      isCurrentUser={isCurrentUser}
                      memberLabel={memberLabel}
                      onView={setSelectedMember}
                      onMessage={handleSendMessage}
                      onRemove={(m) => {
                        const isPending = m.metadata?.status === 'PENDING';
                        const actionName = isPending ? 'revoke this invitation' : `remove this ${memberLabel.toLowerCase()}`;
                        if (window.confirm(`Are you sure you want to ${actionName}?`)) {
                          const invitationId = m.metadata?.invitationId || (m.userId?.startsWith('invite-') ? m.id : null);
                          if (isPending && invitationId) {
                            revokeInvitation.mutate(invitationId);
                          } else {
                            removeMember.mutate(m.id || m.userId);
                          }
                        }
                      }}
                      onRoleChange={(m) => {
                        setRoleChangeMember(m);
                        setNewTargetRole(m.role || 'MEMBER');
                      }}
                      activeActionMenu={activeActionMenu}
                      setActiveActionMenu={setActiveActionMenu}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-none border-2 border-black dark:border-white border-dashed border-black dark:border-white dark:bg-slate-950">
            <div className="w-12 h-12 bg-white rounded-none flex items-center justify-center mx-auto mb-3 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900">
              <Search size={20} className="text-gray-400" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">No {membersLabel.toLowerCase()} found</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Try adjusting your filters or search query.</p>
          </div>
        )}
      </Card>

      {/* Member Detail Modal */}
      <Modal
        isOpen={!!selectedMember}
        onClose={() => setSelectedMember(null)}
        title={`${memberLabel} Profile`}
        className="max-w-md"
      >
        {selectedMember && (() => {
          const currentUserId = user?.id || user?.userId || user?.clerkId;
          const isCurrentUser = Boolean(currentUserId && selectedMember.userId === currentUserId);
          const modalDisplayName = (isCurrentUser && (user?.fullName || user?.email)) 
            ? (user.fullName || user.email)
            : (selectedMember.name || selectedMember.email || (selectedMember.metadata?.status === 'PENDING' || (selectedMember as any).status === 'PENDING' ? 'Pending Invitation' : memberLabel));
          const modalEmail = (isCurrentUser && (user?.email || user?.primaryEmailAddress?.emailAddress))
            ? (user.email || user.primaryEmailAddress?.emailAddress)
            : selectedMember.email;

          return (
          <div className="space-y-6">
            <div className="flex flex-col items-center text-center pb-6 border-b border-black dark:border-white">
              <Avatar userId={selectedMember.userId} initials={modalDisplayName?.[0] || '?'} size="lg" className="mb-4 w-20 h-20 text-2xl" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{modalDisplayName}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{selectedMember.metadata?.department || `Organization ${memberLabel}`}</p>
              
              <div className="flex gap-2">
                <Badge variant={selectedMember.role === 'ADMIN' || selectedMember.role === 'PRESIDENT' ? 'blue' : 'gray'}>
                  {selectedMember.role}
                </Badge>
                <Badge variant={selectedMember.metadata?.status === 'PENDING' ? 'amber' : 'green'}>
                  {selectedMember.metadata?.status || 'ACTIVE'}
                </Badge>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1">
                    <Mail size={10} /> Email
                  </p>
                  {getVisibilityStatus(selectedMember) === 'FULL' ? (
                    <p className="text-sm font-medium text-navy-900 dark:text-white truncate">
                      {modalEmail || 'Not provided'}
                    </p>
                  ) : (
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 italic">
                      <Lock size={10} /> Protected
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1">
                    <Phone size={10} /> Phone
                  </p>
                  {getVisibilityStatus(selectedMember) === 'FULL' ? (
                    <p className="text-sm font-medium text-navy-900 dark:text-white truncate">
                      {selectedMember.metadata?.phone || 'Not provided'}
                    </p>
                  ) : (
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 italic">
                      <Lock size={10} /> Protected
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1">
                  <Clock size={10} /> {memberLabel} Since
                </p>
                <p className="text-sm font-medium text-navy-900 dark:text-white">
                  {safeFormat(selectedMember.joinedAt, 'MMMM do, yyyy')}
                </p>
              </div>

              {getVisibilityStatus(selectedMember) === 'RESTRICTED' && (
                <div className="bg-amber-50 p-3 rounded-none border-2 border-black dark:border-white border-amber-100 flex gap-3">
                  <HelpCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-[10px] text-amber-700 leading-relaxed">
                    Contact details are hidden for privacy. You can reach this person by sending a message through the application.
                  </p>
                </div>
              )}

              {selectedMember.skills && selectedMember.skills.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Skills & Expertise</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedMember.skills.map(skill => (
                      <span key={skill} className="px-2 py-1 bg-white text-gray-600 dark:text-gray-300 rounded-none text-[10px] font-bold uppercase dark:bg-slate-800">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="pt-6 flex gap-3">
              <Button onClick={() => setSelectedMember(null)} variant="secondary" className="flex-1 text-sm">Close</Button>
              {!isCurrentUser && (
                <Button 
                  onClick={() => handleSendMessage(selectedMember)}
                  className="flex-1 text-sm bg-brand-primary"
                  isLoading={createDirectChannel.isPending}
                >
                  Send Message
                </Button>
              )}
            </div>
          </div>
          );
        })()}
      </Modal>

      {/* Role Change Modal */}
      <Modal
        isOpen={!!roleChangeMember}
        onClose={() => setRoleChangeMember(null)}
        title={`Change ${memberLabel} Role`}
        className="max-w-md"
      >
        {roleChangeMember && (
          <div className="space-y-6">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Update organization role for <span className="font-bold text-gray-900 dark:text-white">{roleChangeMember.name || roleChangeMember.email || roleChangeMember.userId}</span>.
            </p>

            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Select New Role</label>
              <select
                value={newTargetRole}
                onChange={(e) => setNewTargetRole(e.target.value)}
                className="w-full h-12 bg-white border-2 border-black dark:border-white rounded-none px-4 text-sm font-bold focus:ring-2 focus:ring-brand-primary dark:bg-slate-900"
              >
                <option value="MEMBER">Member</option>
                <option value="MODERATOR">Moderator</option>
                <option value="ADMIN">Admin</option>
                <option value="TREASURER">Treasurer</option>
                <option value="SECRETARY">Secretary</option>
                <option value="PRESIDENT">President</option>
              </select>
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t border-black dark:border-white">
              <Button variant="secondary" onClick={() => setRoleChangeMember(null)}>Cancel</Button>
              <Button 
                variant="primary" 
                onClick={handleExecuteRoleChange}
                isLoading={updateMemberRole.isPending}
              >
                Save Role
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Invite Modal */}
      {isInviteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <Card className="w-full max-w-md p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {createdInvite ? 'Invitation Sent!' : `Invite new ${memberLabel.toLowerCase()}`}
              </h3>
              <button onClick={handleCloseInviteModal} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            
            {createdInvite ? (
              <div className="space-y-5">
                <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 border-2 border-emerald-500 rounded-none text-emerald-900 dark:text-emerald-200">
                  <div className="flex items-center gap-2 font-bold text-sm">
                    <Check size={18} className="text-emerald-600 dark:text-emerald-400" />
                    Invitation Sent Successfully!
                  </div>
                  <p className="text-xs mt-1 text-emerald-800 dark:text-emerald-300">
                    An email has been sent to <strong>{createdInvite.email}</strong> with instructions to join as a <strong>{createdInvite.role}</strong>.
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Direct Invitation Link
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    You can also copy this link and share it directly with them via chat, Slack, or SMS:
                  </p>
                  <div className="flex gap-2">
                    <Input 
                      readOnly 
                      value={createdInvite.inviteLink || ''} 
                      className="font-mono text-xs bg-gray-50 dark:bg-slate-900 select-all" 
                    />
                    <Button
                      onClick={() => {
                        if (createdInvite.inviteLink) {
                          navigator.clipboard.writeText(createdInvite.inviteLink);
                          toast.success('Invite link copied to clipboard!');
                        }
                      }}
                      className="shrink-0 flex items-center gap-1.5"
                    >
                      <Copy size={16} /> Copy
                    </Button>
                  </div>
                </div>

                <div className="pt-4 flex flex-col sm:flex-row gap-3 border-t border-black dark:border-white">
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      setCreatedInvite(null);
                      setInviteEmail('');
                    }} 
                    className="flex-1"
                  >
                    Invite Another
                  </Button>
                  <Button 
                    onClick={handleCloseInviteModal} 
                    className="flex-1"
                  >
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Email Address</label>
                    <Input 
                      placeholder="email@example.com" 
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      disabled={inviteMutation.isPending}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Assign Role</label>
                    <select 
                      className="w-full bg-white border-2 border-black dark:border-white rounded-none px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-primary/20 dark:bg-slate-900"
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      disabled={inviteMutation.isPending}
                    >
                      {roles.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="pt-4 flex flex-col sm:flex-row gap-3">
                  <Button variant="ghost" onClick={handleCloseInviteModal} className="flex-1" disabled={inviteMutation.isPending}>Cancel</Button>
                  <Button onClick={handleSendInvite} isLoading={inviteMutation.isPending} className="flex-1">Send Invitation</Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}
      {/* Roster CSV Import Modal */}
      <RosterCsvImportModal 
        isOpen={isCsvModalOpen}
        onClose={() => setIsCsvModalOpen(false)}
        nodeId={node?.id || ''}
        roles={roles}
        memberLabel={memberLabel}
        existingEmails={existingEmails}
      />
    </div>
  );
};
